import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { getMutuallyExclusiveUIExperiment } from '@atlassian/search-experiment';

import { type PrimaryProductKey, PrimaryProductKeys } from '../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';

/**
 * A centralised util to check if the 3P Bulk Connect experiment is enabled WITHOUT triggering an exposure.
 * This is useful for cases where we want to check the cohort allocation without logging an exposure.
 */
export const serve3PBulkConnectWithoutExposure = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('3p_bulk_connect_exp', 'cohort', 'control', {
			fireExperimentExposure: false,
		}) === 'test'
	);
};

/**
 * A util to trigger an exposure for the 3P Bulk Connect experiment.
 */
export const triggerExposureFor3PBulkConnect = (): void => {
	FeatureGates.manuallyLogExperimentExposure('3p_bulk_connect_exp');
};

/**
 * A centralised util to check if the Right Rail improvements experiment is enabled WITHOUT triggering an exposure.
 * This is useful for cases where we want to check the cohort allocation without logging an exposure.
 */
export const serveRightRailImprovementsWithoutExposure = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo-search-right-rail-improvements',
			'cohort',
			'control',
			{
				fireExperimentExposure: false,
			},
		) === 'test'
	);
};

/**
 * A util to trigger an exposure for the 3P Bulk Connect experiment.
 */
export const triggerExposureForWeakLinkExp = (): void => {
	FeatureGates.manuallyLogExperimentExposure('enable_google_drive_impersonation_search_in_fps');
};

/**
 * A centralised util to check if the Right Rail improvements experiment is enabled WITHOUT triggering an exposure.
 * This is useful for cases where we want to check the cohort allocation without logging an exposure.
 */
export const serveWeakLinkExpWithoutExposure = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'enable_google_drive_impersonation_search_in_fps',
			'cohort',
			'control',
			{
				fireExperimentExposure: false,
			},
		) === 'test'
	);
};

/**
 * A util to trigger an exposure for the Right Rail improvements experiment.
 */
export const triggerExposureForRightRailImprovements = (): void => {
	FeatureGates.manuallyLogExperimentExposure('rovo-search-right-rail-improvements');
};

/**
 * A centralised util to check if the QF dropdown filter experiment is enabled
 */
export const serveQFDropdownExperiment = (
	primaryProduct?: PrimaryProductKey | null,
	{ fireExperimentExposure = true }: { fireExperimentExposure?: boolean } = {},
): boolean => {
	if (!fg('rovo_qf_dropdown_filters') || !primaryProduct) {
		return false;
	}

	if (primaryProduct === PrimaryProductKeys.Jira) {
		return true;
	}

	return FeatureGates.getExperimentValue(
		'rovo_quick_find_more_filters_exp',
		'isDropdownFiltersEnabled',
		false,
		{ fireExperimentExposure },
	);
};

/**
 * A centralised util to check if the QF filter suggestion experiment is enabled
 */
export const serveQFFilterSuggestionExperiment = (
	primaryProduct?: PrimaryProductKey | null,
	{ fireLayerExposure = true }: { fireLayerExposure?: boolean } = {},
): boolean => {
	if (primaryProduct !== PrimaryProductKeys.Jira) {
		return false;
	}
	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_qf_filter_suggestion_exp',
		'rovo_search_qf_experience_layer',
		{ fireLayerExposure },
	);
	return (
		layer?.get(
			'isFilterSuggestionEnabled',
			false,
			(value: unknown): value is boolean => typeof value === 'boolean',
		) ?? false
	);
};

/**
 * A centralised util to check if the QF app selector experiment is enabled
 */
export const serveQFAppSelectorExperiment = (
	primaryProduct?: PrimaryProductKey | null,
	{ fireExperimentExposure = true }: { fireExperimentExposure?: boolean } = {},
): boolean => {
	if (!fg('rovo_qf_app_selector') || !primaryProduct) {
		return false;
	}

	if (primaryProduct === PrimaryProductKeys.Jira) {
		return true;
	}

	return FeatureGates.getExperimentValue(
		'rovo_quick_find_more_filters_exp',
		'isAppSelectorEnabled',
		false,
		{ fireExperimentExposure },
	);
};

/**
 * A centralised util to check if the SearchPageRelay experiment.
 * This will trigger an exposure if fireExperimentExposure is true.
 * Defaults to false to avoid unnecessary exposures.
 */
export const serveSearchPageRelayExp = (
	primaryProduct: PrimaryProductKey | null,
	fireExperimentExposure = false,
	queryParams?: Record<string, any>,
): boolean => {
	const serveSearchPageRelayExpIgnoringCQL = (): boolean => {
		// This FG is used to force enable Relay for e2e testing purposes
		if (fg('search-page-relay-e2e-testing')) {
			return true;
		}

		if (primaryProduct === PrimaryProductKeys.Confluence) {
			return (
				FeatureGates.getExperimentValue<string>('search_page_relay_exp', 'cohort', 'control', {
					fireExperimentExposure,
				}) === 'test'
			);
		}
		if (primaryProduct === PrimaryProductKeys.Jira) {
			return (
				FeatureGates.getExperimentValue<string>('search_page_relay_jira', 'cohort', 'control', {
					fireExperimentExposure,
				}) === 'test'
			);
		}

		return fg('search-page-relay');
	};

	if (queryParams && queryParams['cql']) {
		return (
			serveSearchPageRelayExpIgnoringCQL() &&
			FeatureGates.getExperimentValue<boolean>('rovo_search_page_relay_cql', 'isEnabled', false)
		);
	} else {
		return serveSearchPageRelayExpIgnoringCQL();
	}
};

export const serveBootstrapProviderExp = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo-search-remove-rss-bootstrap-exp',
			'cohort',
			'control',
			{
				fireExperimentExposure: true,
			},
		) === 'test'
	);
};

// A centralised util to check if the search_user_right_rail_3p_personalization_v1 is enabled and logging an exposure.
export const serveRightRailRecommendationsImprovementsWithExposure = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'search_user_right_rail_3p_personalization_v1',
			'cohort',
			'control',
			{
				fireExperimentExposure: true,
			},
		) === 'test'
	);
};

export const serveSearchPageTimeoutsUX = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('search_page_timeouts_ux', 'cohort', 'control', {
			fireExperimentExposure: true,
		}) === 'test'
	);
};

export const serveRegionSteeringExperiment = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'search_page_region_steering_experiment',
			'cohort',
			'control',
		) === 'test'
	);
};

export const serveConfluenceSpaceIconUpdate = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>('confluence_space_icon_update', 'isEnabled', false) ===
		true
	);
};

export const serveSearchPageRenderingOptimisations = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'rovo_search_page_rendering_optimisations',
			'isEnabled',
			false,
			{
				fireExperimentExposure: true,
			},
		) === true
	);
};

// Experiment: https://hello.atlassian.net/wiki/x/okE3bgE
export const serve3PClickIngestionExperiment = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: true,
	},
): boolean => {
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return (
		isEligible3PUser &&
		FeatureGates.getExperimentValue<string>(
			'3p_clicks_in_fps_as_qf_recents',
			'cohort',
			'control',
			options,
		) === 'test'
	);
};

export const serveSearchFixFiltersLayoutShift = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'search_fix_filters_layout_shift',
			'isEnabled',
			false,
		) === true
	);
};


/**
 * A centralised util to check if the Quick Find 3P boosting experiment is enabled.
 * This experiment prioritizes 3rd party documents in the recent activities section.
 * Note: This is a standalone experiment (not mutually exclusive with other Quick Find experiments).
 */
export const serveQuickFind3PBoostingExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('quick_find_3p_boosting_experiment', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};
